import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import Input from 'dpl/form/components/Input';
import FormContext from 'dpl/shared/form/utils/context';
import useForm from 'dpl/shared/form/hooks/useForm';
import { CONSTRAINT_TYPES } from 'dpl/shared/validations/constraints';
import Checkbox from 'dpl/form/components/Checkbox';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import {
  buyerProfileUpdateMutationDefinition,
  buyerProfileEditResourceDefinition
} from 'dpl/shared/buyer/utils/queryDefinitions';
import SettingsPhoneFields from 'dpl/user_account/components/SettingsPhoneFields';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import { set, remove } from 'dpl/shared/utils/object';
import { flattenFormErrors } from 'dpl/buyer_account/utils';

export const VALIDATION_SCHEMA = {
  is_over_eighteen: CONSTRAINT_TYPES.BOOLEAN,
  'user_attributes.first_name': CONSTRAINT_TYPES.NON_EMPTY,
  'user_attributes.last_name': CONSTRAINT_TYPES.NON_EMPTY,
  'address_attributes.postal_code': CONSTRAINT_TYPES.REQUIRED_ZIP,
  'user_attributes.phone_numbers_attributes[].number':
    CONSTRAINT_TYPES.TELEPHONE
};

export default function AdditionalInfoForm({
  onSuccessfulSubmission,
  createUserFn
}) {
  const currentUser = useCurrentUser();
  const buyerProfileId = currentUser.value.data?.buyer_profile_id;
  const buyerProfileQuery = useResourceQuery(
    buyerProfileEditResourceDefinition(buyerProfileId)
  );
  const { mutateAsync: updateBuyerProfile } = useResourceMutation({});

  const {
    isSubmitting,
    handleSubmission,
    contextValue,
    isValid,
    formErrors,
    formState,
    didSubmissionFail,
    setValue,
    reset
  } = useForm({
    defaultFormState: buyerProfileQuery.value.buyer_profile,
    mapFormStateToValidationSchema: () => VALIDATION_SCHEMA,
    onSubmit: async _formState => {
      let _buyerProfileId = currentUser.value.data?.buyer_profile_id;

      if (createUserFn) {
        const user = await createUserFn(_formState.user_attributes);

        if (!user) {
          return false;
        }
        // if just created a user, don't try to update it via nested attributes
        // bc rails will try to create a new user record here where we don't
        // yet have the user's ID in the payload
        _formState = remove(_formState, 'user_attributes');
        _buyerProfileId = user.buyer_profile_id;
      }

      return updateBuyerProfile({
        ...buyerProfileUpdateMutationDefinition(_buyerProfileId),
        body: {
          buyer_profile: _formState
        }
      });
    }
  });

  useEffect(() => {
    if (buyerProfileQuery.isSuccess || currentUser.isLoggedIn === false) {
      // Updates phone_numbers_attributes if it is empty in order to show
      // empty phone number and phone number type inputs
      let buyerProfile = buyerProfileQuery.value.buyer_profile;

      if (buyerProfile.user_attributes.phone_numbers_attributes.length === 0) {
        buyerProfile = set(
          buyerProfile,
          'user_attributes.phone_numbers_attributes',
          [
            {
              number_type: 'mobile'
            }
          ]
        );
      }

      if (buyerProfile.is_over_eighteen == null) {
        buyerProfile = set(buyerProfile, 'is_over_eighteen', true);
      }

      reset(buyerProfile);
    }
  }, [buyerProfileQuery.isSuccess, currentUser.isLoggedIn]);

  async function handleSubmit(e) {
    e.preventDefault();

    const ret = await handleSubmission();

    if (ret?.response?.ok) {
      await onSuccessfulSubmission();

      if (buyerProfileId) {
        await buyerProfileQuery.refetch();
      }
    }
  }

  return (
    <div className="BuyerAccountAdditionalInfoForm">
      <h5 className="mb8 tc">Complete your profile</h5>
      <FormContext.Provider value={contextValue}>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-6 mb6">
              <Input
                name="user_attributes.first_name"
                label="First name"
                placeholder="Jane"
              />
            </div>

            <div className="col-6 mb6">
              <Input
                name="user_attributes.last_name"
                label="Last name"
                placeholder="Smith"
              />
            </div>
          </div>

          <SettingsPhoneFields
            formState={formState.user_attributes}
            formErrors={flattenFormErrors(formErrors, 'user_attributes')}
            displayErrors={didSubmissionFail}
            formFieldName="user_attributes"
            disableSecondary
            onChange={(fieldName, value) => {
              if (fieldName.target) {
                ({ name: fieldName, value } = fieldName.target);
              }
              setValue(`user_attributes.${fieldName}`, value);
            }}
          />

          <div className="mb6">
            <Input
              name="address_attributes.postal_code"
              label="Zip code"
              type="number"
            />
          </div>

          <div className="mb6">
            <Checkbox
              name="is_over_eighteen"
              title="I am over 18 years old"
              id="is_over_eighteen"
              checked={Boolean(formState.is_over_eighteen)}
              value=""
              onChange={() =>
                setValue('is_over_eighteen', !formState.is_over_eighteen)
              }
            />
          </div>

          <div className="mb6">
            <button
              data-test-id="additional_info_submit"
              type="submit"
              disabled={isSubmitting || !isValid}
              className="button button--primary pv2 w-100"
            >
              Continue
            </button>
          </div>
        </form>
      </FormContext.Provider>
    </div>
  );
}

AdditionalInfoForm.propTypes = {
  onSuccessfulSubmission: PropTypes.func.isRequired,
  createUserFn: PropTypes.func
};

AdditionalInfoForm.defaultProps = {
  createUserFn: null
};
